import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import GenreShow from "./components/GenreShow";
import Footer from "../../components/Footer/Footer";
import { useHoverBackground } from "../../utils/Actions/useHoverBackground";
import { Helmet } from "react-helmet";
import { useActiveGenre } from "../../utils/Genre/activeGenreState";
import { getGenrePageData } from "../../utils/api/helpers/pages";
import PageLoader from "../../utils/Ui/PageLoader";
import { getGuildHomePath } from "../../utils/Guild/guildUtils";
import { useGuild } from "../../utils/Guild/useGuild";
import { ORDER_FIRST } from "../../data/globalConstants";

gsap.registerPlugin(ScrollTrigger);

export default function Genre() {
  const { genreSlug } = useParams();
  const [genreData, setGenreData] = useState(null);
  const [genreName, setGenreName] = useState("");
  const [themeColor, setThemeColor] = useState(null);
  const [randIndex, setRandIndex] = useState(0);

  // Split readiness flags
  const [isDataReady, setIsDataReady] = useState(false);
  const latestGenreSlugRef = useRef(genreSlug);

  // Derived loader flag (only false when BOTH are ready)
  const isLoading = !isDataReady;

  const containerRef = useRef(null);
  const featuredImageRef = useRef(null);
  const pageContentRef = useRef(null);
  const [activeGenre] = useActiveGenre();
  const guild = useGuild();

  // Guild sub-sites show every title at featured size rather than splitting on
  // the CMS "featured" flag, which belongs to the main site.
  const allFeatured = Boolean(guild);

  const { featuredShows, unFeaturedShows } = useMemo(() => {
    const featured = genreData?.featuredShows || [];
    const unfeatured = genreData?.unFeaturedShows || [];
    const hasChildren = Boolean(genreData?.children?.length);

    // With child genres the extra titles already appear in their own grouped
    // sections below, so only merge when there aren't any.
    if (!allFeatured || hasChildren) {
      return { featuredShows: featured, unFeaturedShows: unfeatured };
    }

    const sortTitle = (show) =>
      String(show?.title || "")
        .trim()
        .replace(/^the\s+/i, "")
        .toLowerCase();

    const merged = [...featured, ...unfeatured].sort((a, b) =>
      sortTitle(a).localeCompare(sortTitle(b)),
    );

    return { featuredShows: merged, unFeaturedShows: [] };
  }, [genreData, allFeatured]);

  // Featured titles stay in one row; they're ordered so that titles sharing a
  // child genre (e.g. Limited Series, then Movie) sit together, with the child
  // genre showing as the label under the key art. Titles with no child genre
  // come last. Within a group it's alphabetical, ignoring a leading "The".
  const orderedFeaturedShows = useMemo(() => {
    const children = genreData?.children || [];

    const parseGenres = (show) => {
      try {
        return JSON.parse(`[${show?.genres?.[0] ?? ""}]`);
      } catch (error) {
        return [];
      }
    };

    // On a sub-site every title is featured, so child-genre titles join the
    // same row. They arrive without genre data, so label them with the child
    // genre they came from.
    const childShows = allFeatured
      ? children.flatMap((child) =>
          (child.shows || []).map((show) => ({
            ...show,
            genres: show?.genres?.length
              ? show.genres
              : [JSON.stringify({ title: child.title, slug: child.slug })],
          })),
        )
      : [];

    const shows = [...featuredShows, ...childShows];

    if (!children.length) return shows;

    const childOrder = new Map(children.map((child, i) => [child.slug, i]));

    const groupRank = (show) => {
      const match = parseGenres(show).find((genre) =>
        childOrder.has(genre?.slug),
      );

      return match ? childOrder.get(match.slug) : children.length;
    };

    const sortTitle = (show) =>
      String(show?.title || "")
        .trim()
        .replace(/^the\s+/i, "")
        .toLowerCase();

    return shows
      .slice()
      .sort(
        (a, b) =>
          groupRank(a) - groupRank(b) ||
          sortTitle(a).localeCompare(sortTitle(b)),
      );
  }, [genreData, featuredShows, allFeatured]);

  const navigate = useNavigate();

  const hoverProps = useHoverBackground(
    activeGenre?.theme?.colors?.dark?.hex,
    activeGenre?.theme?.colors?.light?.hex,
  );

  const hasAwards = false;

  useEffect(() => {
    latestGenreSlugRef.current = genreSlug;
  }, [genreSlug]);

  // Fetch genre data
  useEffect(() => {
    if (!genreSlug) return;

    const requestedGenreSlug = genreSlug;

    setIsDataReady(false);
    setGenreData(null);
    setGenreName("");

    getGenrePageData(genreSlug)
      .then((res) => {
        if (requestedGenreSlug !== latestGenreSlugRef.current) return;

        const data = res?.data;

        const hasFeaturedShows = data?.featuredShows?.length > 0;
        const hasUnfeaturedShows = data?.unFeaturedShows?.length > 0;
        const hasChildShows = data?.children?.some(
          (child) => child?.shows?.length > 0,
        );

        const hasGenreData =
          data && (hasFeaturedShows || hasUnfeaturedShows || hasChildShows);

        if (!hasGenreData) {
          navigate(getGuildHomePath(), { replace: true });
          return;
        }

        setGenreName(data.title || "");
        setGenreData(data);
        setIsDataReady(true);
      })
      .catch(() => {
        if (requestedGenreSlug !== latestGenreSlugRef.current) return;

        navigate(getGuildHomePath(), { replace: true });
      });
  }, [genreSlug, navigate]);

  // Set theme color and random index
  useEffect(() => {
    if (!activeGenre) return;

    setThemeColor(activeGenre?.theme?.colors?.dark?.rgb);
    // setRandIndex(getRandomIndex(activeGenre?.featuredShows));
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [activeGenre]);

  // GSAP animations
  useGSAP(
    () => {
      if (!activeGenre || isLoading) return;

      const container = containerRef.current;
      const bgImg = featuredImageRef.current;
      if (!container || !bgImg) return;

      const gridItems = container.querySelectorAll(".genre-show");

      if (gridItems.length) {
        gsap.set(gridItems, {
          y: 20,
          opacity: 0,
          filter: "blur(10px)",
        });

        gsap.to(gridItems, {
          y: 0,
          opacity: 1,
          filter: "blur(0px)",
          duration: 1,
          ease: "power3.out",
          stagger: 0.08,
        });
      }

      gsap.to(bgImg, {
        filter: "blur(10px)",
        opacity: 0.5,
        ease: "none",
        scrollTrigger: {
          trigger: container,
          start: "top top",
          end: "bottom bottom",
          scrub: true,
          invalidateOnRefresh: true,
        },
      });

      ScrollTrigger.refresh();
    },
    {
      dependencies: [activeGenre, randIndex, isLoading],
      scope: containerRef,
    },
  );

  // Margin calculation based on featured image size
  useEffect(() => {
    const featuredEl = featuredImageRef.current;
    const pageEl = pageContentRef.current;

    if (!featuredEl || !pageEl) return;
    let rafId = 0;

    const applyMargin = () => {
      const page = pageContentRef.current;
      if (!page) return;

      const imgHeight = featuredEl.offsetHeight * 0.6;
      page.style.marginTop = `${imgHeight}px`;
    };

    const resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(applyMargin);
    });

    resizeObserver.observe(featuredEl);
    applyMargin();

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, [activeGenre, randIndex]);

  return (
    <>
      <PageLoader isLoading={isLoading} />
      <div
        className="page-container genre-container"
        ref={containerRef}
        style={{
          background: `linear-gradient(
            to right,
            rgba(${themeColor},0.7) 0%,
            rgba(0,0,0,1) 50%,
            rgba(${themeColor},0.7) 100%
          )`,
        }}
      >
        {genreName && (
          <Helmet>
            <title>{`${genreName} | Netflix Series FYC`}</title>
            <meta name="description" content={genreName} />
          </Helmet>
        )}

        <div className="page-content" ref={pageContentRef}>
          <div className="width-ctrl">
            <div
              className={`genre-grid${allFeatured ? " is-all-featured" : ""}`}
              key={genreSlug}
            >
              {!isLoading &&
                genreData &&
                orderedFeaturedShows?.map((show) => {
                  return (
                    <GenreShow
                      key={show.slug}
                      genreSlug={genreSlug}
                      activeGenre={activeGenre}
                      show={show}
                      hasAwards={hasAwards}
                      featured={true}
                    />
                  );
                })}
            </div>

            {!isLoading &&
            genreData &&
            unFeaturedShows?.length &&
            !genreData?.children?.length ? (
              <div className={"subgenre-container"}>
                <h4 className={"subgenre-title"}>{genreData?.title}</h4>
                <div className="genre-grid subgenre-grid" key={genreSlug}>
                  {unFeaturedShows?.map((show) => (
                    <GenreShow
                      key={show.slug}
                      genreSlug={genreSlug}
                      activeGenre={activeGenre}
                      show={show}
                      hasAwards={hasAwards}
                      featured={false}
                    />
                  ))}
                </div>
              </div>
            ) : null}

            {/* Main site only: on a sub-site these titles are already in the
                featured row above, grouped by child genre. */}
            {!isLoading &&
              !allFeatured &&
              genreData &&
              genreData?.children?.map((genre, idx) =>
                genre?.shows?.length ? (
                  <div key={idx} className={"subgenre-container"}>
                    <h4 className={"subgenre-title"}>{genre.title}</h4>
                    <div className="genre-grid subgenre-grid" key={genreSlug}>
                      {genre.shows.map((show) => (
                        <GenreShow
                          key={show.slug}
                          genreSlug={genreSlug}
                          activeGenre={activeGenre}
                          show={show}
                          hasAwards={hasAwards}
                          featured={false}
                        />
                      ))}
                    </div>
                  </div>
                ) : null,
              )}
          </div>
        </div>

        <Footer type={"genre"} />

        <div
          className={"genre-background " + (activeGenre ? "loaded" : "")}
          style={{
            background: `linear-gradient(
            to right,
            rgba(${themeColor},0.7) 0%,
            rgba(0,0,0,1) 50%,
            rgba(${themeColor},0.7) 100%
          )`,
          }}
          aria-hidden={"true"}
        />
      </div>
    </>
  );
}
